<template>
    <div :class="[css.container, mask && css.mask]" ref="container">
        <table :class="css.body" :style="{ maxWidth: width && `${width}px` }">
            <!-- * 尺寸分配 -->
            <colgroup>
                <col v-for="row in rows" :key="row.key" :style="utils.size(row)" />
            </colgroup>

            <!-- * 表格头部区域 -->
            <thead>
                <!-- * 表头 -->
                <tr v-for="(colspan, key) in colspans" :key="key">
                    <th v-for="row in colspan" :key="row.key" :colspan="row.colspan" :rowspan="row.rowspan" v-bind="utils.rows(row)">
                        <div :class="css.column" :data-key="row.key" data-thead="true">
                            <!-- * 内容 -->
                            <slot :name="`${row.slot || row.key}_head`">
                                <div v-html="row.name"></div>
                            </slot>

                            <!-- * 控件 -->
                            <div :class="css.controls" v-if="row.control">
                                <!-- * 排序控件 -->
                                <div :class="css.sort" v-if="row.control == 'sort'" @click="methods.sort(row.key, row.reverse)">
                                    <div :class="[css.trigger, row.key == sort.key && sort.value == 'asc' && css.active]"></div>
                                    <div :class="[css.trigger, row.key == sort.key && sort.value == 'desc' && css.active]"></div>
                                </div>

                                <!-- * 高级控件 -->
                                <template v-if="row.control == 'advanced'">
                                    <ui-tooltip :class="css.advanced" :class-extra-name="css.view" align="center" trigger="click" direction="bottom">
                                        <ui-icon name="filter" />
                                        <template #content>
                                            <template v-if="row.selectors?.length">
                                                <!-- * 全选按钮 -->
                                                <ui-checkbox
                                                    v-model="checkboxs[row.key]"
                                                    v-bind="binds.checkboxs"
                                                    @change="methods.checkboxs(row.key)">
                                                    {{ $t("SelectAll") }}
                                                </ui-checkbox>
                                                <!-- * 多选列表 -->
                                                <ui-checkbox-group v-model="checkbox[row.key]" @change="methods.checkbox(row.key)">
                                                    <ui-checkbox :class="css_normalize.checkbox" :value="value.value" v-for="value in row.selectors">
                                                        <img v-if="value.src" :src="value.src" alt="图标" />
                                                        <span>{{ value.name }}</span>
                                                    </ui-checkbox>
                                                </ui-checkbox-group>
                                            </template>
                                        </template>
                                    </ui-tooltip>
                                </template>
                            </div>

                            <!-- * 备注控件 -->
                            <div :class="css.controls" v-if="row.explain">
                                <ui-tooltip :class="css.explain" :width="row['explain-width']" align="start" direction="bottom">
                                    <ui-icon name="mark" />
                                    <template #content>
                                        <div style="word-wrap: break-word" v-html="row.explain"></div>
                                    </template>
                                </ui-tooltip>
                            </div>
                        </div>
                    </th>
                </tr>

                <!-- * 检索控制器 -->
                <tr v-if="searchHeader">
                    <th v-for="row in rows">
                        <default-search
                            :class="css.search"
                            v-model="search[row.key]"
                            size="small"
                            width="100%"
                            :placeholder="$t('Search', 2)"
                            @input="methods.search" />
                    </th>
                </tr>
            </thead>

            <!-- * 表格内容区域 -->
            <render-content />
        </table>
    </div>
</template>

<script lang="tsx" setup>
//* 组件库
import { UiIcon, UiTooltip, UiCheckbox, UiCheckboxGroup } from "various-ui";
//* 公共组件
import { DefaultSearch } from "@/components/controls";
//* 组件属性
import { DiagramsEmits, DiagramsPropsOption, type DiagramsOption } from "./index";
import { useComposable } from "./src/composable";
//* 样式
import css from "./index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";

//* 组件属性注册
const slots = defineSlots();
const emits = defineEmits(DiagramsEmits);
const define = defineProps(DiagramsPropsOption);

//* 组合函数
const { vars, refs, nodes, utils, binds, watchs, methods, computeds } = useComposable(define, emits);
const { colspans, rows } = computeds;
const { container } = nodes;
const { mask, sort, search, checkbox, checkboxs } = refs;

//* 组件挂载时注册事件
onMounted(() => {
    //* 注册滚动事件
    container.value?.addEventListener("scroll", utils.scroll);
    //* 创建观察者
    vars.observer = new ResizeObserver(utils.resize);
    if (container.value) {
        vars.observer.observe(container.value);
    }
});

//* 组件卸载前销毁事件
onBeforeUnmount(() => {
    //* 销毁侦听器
    watchs.colspans();
    //* 销毁观察者
    vars.observer?.disconnect();
    //* 销毁节流阀
    vars.timer && clearTimeout(vars.timer);
    //* 销毁滚动事件
    container.value?.removeEventListener("scroll", utils.scroll);
});

defineExpose({ sort: methods.sort });

const renderContent = defineComponent({
    render() {
        //* 渲染函数（内容）
        const renderBodys = () => {
            if (define.data.length) return define.data.map((value, index) => renderBody(value, index));
            else {
                return (
                    <tr>
                        <td colspan={rows.value.length} style="text-align: center">
                            {slots.empty ? slots.empty() : define.empty}
                        </td>
                    </tr>
                );
            }
        };

        //* 渲染函数（行）
        const renderBody = (value: any, index: number) => {
            return <tr {...(define.externalTrAttrs?.(define.data, value, index) || {})}>{rows.value.map((row) => renderCell(value, index, row))}</tr>;
        };

        //* 渲染函数（单元格）
        const renderCell = (value: any, index: any, row: DiagramsOption) => {
            //* 合并单元格的数量
            const merges = utils.column(value, index, row);
            if (merges.rowspan === 0 || merges.colspan === 0) return;
            else {
                return (
                    <td {...merges}>
                        <div class={css.column}>
                            {row.slot ? slots[row.slot]?.({ data: value }) : slots[row.key] ? slots[row.key]?.({ data: value }) : value[row.key]}
                        </div>
                    </td>
                );
            }
        };

        return <tbody class={css.bodys}>{renderBodys()}</tbody>;
    },
});
</script>
